import { useEffect, useRef, useState, type ReactNode } from 'react';
import { teamFilter } from '../use-teams';
import {
  formatHours,
  loadOf,
  type ResourceItem,
  type ResourceLineItem,
  type ResourceLoad,
  type ResourceRow,
  type ResourceView,
} from '@forecastify/shared';
import {
  AvatarFace,
  Button,
  Icon,
  ListToolbar,
  Skeleton,
  Tooltip,
  LandscapeOnly,
  useIsMobile,
} from '@forecastify/ui';
import { useI18n, useT, type Translate } from '../../i18n';
import { useWorkspaceNavigate } from '../../workspace-path';
import { identityColor, initialsOf } from '../identity';
import { useAvatars } from '../use-avatar';
import { LineItemInfo } from './line-item-summary';
import { LineItemOverview } from './line-item-overview';
import { AllocationCell, type CellTarget } from './allocation-cell';
import {
  LineItemPicker,
  PersonPicker,
  PickerTrigger,
  type PickableLineItem,
} from './line-item-picker';
import { RangeControl } from './range-control';
import { PeopleGridSkeleton } from './grid-skeleton';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ ⚠️⚠️ ERŐFORRÁS — the mockup's `dashres`. C53.                             │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * The same spreadsheet as Logolás, measured off the rendered mockup and identical to it by
 * construction — 240px person column sticky left, a 104px line-label column sticky at 240, 104px
 * span columns, 32px lines, a sticky `bg-muted` header with the current span tinted. Where that one
 * asks *what happened*, this asks *what is planned*.
 *
 * ⚠️⚠️ **THE MIDDLE LINE IS THE ONLY ONE ANYBODY EDITS, AND IT IS NOT EDITABLE HERE.** *Kiosztott*
 * is the sum over every line item, so typing into it would be typing into a total — the planner
 * would have to guess which of three allocations the number belonged to. The mockup makes the same
 * choice: its editable cells are the ones inside an expanded row, each against ONE line item. So
 * the summary line is read-only and the chevron is not decoration.
 *
 * ⚠️ **A cell commits a SPAN, not a day.** The value typed is the total for that column, and the
 * server spreads it over the person's working days — `allocation.service.ts` and the kernel behind
 * it. Which is why a week column accepts `20` and a day column accepts `4`, with no unit anywhere
 * on the screen: the column IS the unit.
 */

/** The mockup's own three, and `none` for a column somebody cannot work at all. */
const LOAD_COLOR: Readonly<Record<ResourceLoad, string | undefined>> = {
  under: 'var(--load-under)',
  ok: 'var(--load-ok)',
  over: 'var(--load-over)',
  none: undefined,
};

/** One of the two hover tools on a cell. Small, quiet, and only ever seen on the cell under the pointer. */
function RangeTool({
  name,
  label,
  onClick,
}: {
  readonly name: 'copy' | 'trash';
  readonly label: string;
  readonly onClick: () => void;
}) {
  return (
    <Tooltip asChild content={label}>
      <button
        type="button"
        aria-label={label}
        className="text-muted-foreground hover:bg-accent hover:text-foreground flex size-5 items-center justify-center rounded transition-colors"
        onClick={onClick}
      >
        <Icon name={name} size={11} />
      </button>
    </Tooltip>
  );
}

/**
 * ⚠️ **The planner's refusals that have their own sentence**, for both routes that write through
 * this grid — null for the rest, which each route words its own way. Branches on `code`, never on
 * `message` (rule 5); the name in P-1's comes from `details`, as the task dialog reads it.
 */
export function allocationRefusal(
  t: Translate,
  error: { code?: string; details?: { code?: string; message?: string }[] } | undefined,
): string | null {
  const detail = error?.details?.[0];
  if (detail?.code === 'no_working_day') return t('resource.error.noWorkingDay');
  if (error?.code === 'assignee_not_a_project_member') {
    return t('resource.error.notAMember', { name: detail?.message ?? '' });
  }
  return null;
}

/** A booking somebody has asked for and not yet given an hour to. See `itemsFor`. */
interface Draft {
  readonly memberId: string;
  readonly lineItemId: string;
  /** How many bookings the pair held when this was asked for; it retires when it holds more. */
  readonly baseline: number;
}

/** A deliverable's row: its bookings summed into one item, and the bookings themselves. */
type Group = ResourceItem & { bookings: ResourceItem[] };

/** U-15 · the project tab's two groupings. People is the dashboard's, and the default. */
export type ResourceGrouping = 'people' | 'items';

function tint(color: string | undefined): string | undefined {
  /* ⚠️ 12% of the verdict's own colour — `tint(col, 12)`, as Logolás does. A solid fill would make
     the figures unreadable and a border would move them. */
  return color === undefined ? undefined : `color-mix(in oklch, ${color} 12%, transparent)`;
}

/**
 * ⚠️ **The address needs BOTH halves.** A line item lives at
 * `/contracts/:contractIdentifier/:lineItemIdentifier` (R-57), and a booking whose contract the
 * reader cannot see carries neither — so it gets no link rather than a broken one, which is what
 * the summary panel's optional `onOpen` is for.
 */
function openLineItem(go: (path: string) => void, item: ResourceItem): { onOpen?: () => void } {
  const contract = item.contractIdentifier;
  const lineItem = item.lineItemIdentifier;
  if (contract === null || lineItem === null) return {};
  return {
    onOpen: () => {
      go(`/contracts/${contract}/${lineItem}`);
    },
  };
}

export function ResourceScreen(props: {
  /** Until the first answer: skeleton rows and no figures. */
  readonly loading?: boolean;
  /**
   * The period requested — shown by the control at once, where `view.from`/`to` arrive only with
   * the answer (14 Oct 2026: UAT read *Időszak* for seven seconds). Absent, the view's own span.
   */
  readonly range?: { readonly from: string; readonly to: string };
  readonly view: ResourceView;
  readonly managers: readonly { readonly id: string; readonly name: string }[];
  readonly projects: readonly { readonly id: string; readonly name: string }[];
  readonly contracts: readonly { readonly id: string; readonly label: string }[];
  /**
   * Everything a person could be planned onto — open line items, with where they live, what they
   * are estimated at and when they run. The last two are what tell two deliverables of the same
   * name on different contracts apart; see `line-item-picker.tsx`.
   */
  readonly lineItems: readonly PickableLineItem[];
  readonly managerId: string;
  readonly projectId: string;
  readonly contractId: string;
  readonly load: string;
  readonly overtimeOnly: boolean;
  readonly query: string;
  readonly canManage: boolean;
  /**
   * ⚠️ **Item 29's global overview, on the PROJECT-scoped page only.** The workspace grid's
   * table would be every deliverable of every project, which answers a question nobody asked.
   */
  readonly overview?: boolean;
  /**
   * ⚠️⚠️ **U-15 · BY PEOPLE OR BY LINE ITEM, AND ONLY THE PROJECT'S TAB OFFERS THE CHOICE.**
   * Without `onGrouping` there is no switch and the grid is the people grid, which is what the
   * dashboard draws. The line-item grouping reads `view.projectLineItems`, which only a project's
   * view carries.
   */
  readonly grouping?: ResourceGrouping;
  readonly onGrouping?: (grouping: ResourceGrouping) => void;
  readonly serverError: string | null;
  /**
   * ⚠️ **The project's tab draws its OWN four figures above this one.** Six workspace figures
   * under four project ones would be ten numbers over one grid — and two of the six, *how many
   * people are over-planned*, are resourcing questions a project page cannot answer, because its
   * rows are one team rather than everybody.
   */
  readonly hideStats?: boolean;
  /**
   * The workspace's ACTIVE overtime types, for the row menu. Empty is an ordinary answer — a
   * workspace that has not defined any simply gets no overtime entries in the menu.
   */
  readonly overtimeTypes: readonly {
    readonly id: string;
    readonly name: string;
    readonly multiplier: string;
  }[];
  /**
   * ⚠️ **The booking's own fields.** They used to be reachable only from a row menu, under a note
   * arguing that a per-CELL control would read as *set overtime for this week* and set it for the
   * whole plan. `allocation-cell.tsx` explains why that was the wrong conclusion — the pop-over is
   * where a pair's SEVERAL bookings are enumerated, which no row menu can express — and each entry
   * there says which booking it edits.
   */
  readonly onPatchItem: (
    item: ResourceItem,
    patch: {
      overtimeTypeId?: string | null;
      distribution?: 'even' | 'earliest';
      booking?: 'hard' | 'soft';
      /** 27 Oct 2026 · the column a distribution change re-spreads (`patchAllocationRequestSchema`). */
      fromDate?: string;
      toDate?: string;
    },
  ) => void;
  readonly onDeleteItem: (item: ResourceItem) => void;
  readonly onManager: (id: string) => void;
  /** R-60 · the team filter — absent where the screen is embedded without one. */
  readonly teams?: readonly { readonly id: string; readonly name: string }[];
  readonly teamId?: string;
  readonly onTeam?: (id: string) => void;
  readonly onProject: (id: string) => void;
  readonly onContract: (id: string) => void;
  readonly onLoad: (value: string) => void;
  readonly onOvertimeOnly: (value: boolean) => void;
  /**
   * Phase 6 · DM-5 · show the tentative bookings deals made on real people. Absent, the chip is
   * not offered (a reader without `deal.view`, or a screen that does not ask for them).
   */
  readonly showTentative?: boolean;
  readonly onShowTentative?: (value: boolean) => void;
  readonly onQuery: (value: string) => void;
  readonly onMode: (mode: 'week' | 'day') => void;
  readonly defaultRange: { readonly from: string; readonly to: string };
  readonly onRange: (from: string, to: string) => void;
  readonly onOpenPerson: (memberId: string) => void;
  readonly onWrite: (input: {
    /**
     * ⚠️ **Which booking, and the three states are three intentions.** An id targets one; absent
     * means the pair, valid while it holds exactly one; `null` asks for a new one. See
     * `saveAllocationRequestSchema`.
     */
    allocationId?: string | null;
    memberId: string;
    lineItemId: string;
    fromDate: string;
    toDate: string;
    hours: number;
  }) => void;
  /**
   * ⚠️ **A range, as ONE write.** The copy and clear tools touch every column between two cells
   * — sixty-odd in a quarter of days — and sixty requests would be sixty redraws and a plan that
   * can end up half-applied. `POST /allocations/batch`.
   */
  readonly onWriteMany: (
    writes: readonly {
      allocationId?: string | null;
      memberId: string;
      lineItemId: string;
      fromDate: string;
      toDate: string;
      hours: number;
    }[],
  ) => void;
}) {
  const t = useT();
  /* ⚠️ R-38 · workspace-relative, so the first segment is supplied — item 28's link. */
  const go = useWorkspaceNavigate();
  const { locale } = useI18n();
  const [open, setOpen] = useState<readonly string[]>([]);
  /**
   * ⚠️ **Line items added to a person before anything is typed into them.** A new allocation
   * cannot exist until a cell carries hours — the server deletes one with no days — so the row has
   * to exist on the client first, which is the mockup's own `resAdded`.
   */
  const [drafts, setDrafts] = useState<readonly Draft[]>([]);
  /**
   * ⚠️⚠️ **THE CELL THE POP-OVER IS OPEN ON, AND THE ELEMENT IT HANGS FROM.** The anchor is
   * captured from the click rather than held as a ref per cell: a quarter-view of twenty people is
   * upwards of a thousand cells, and a thousand refs to position one surface is a thousand objects
   * kept alive to answer a question only one of them is ever asked.
   */
  const [cell, setCell] = useState<CellTarget | null>(null);
  const cellAnchor = useRef<HTMLElement | null>(null);
  /**
   * ⚠️⚠️ **A RANGE OPERATION IS TWO CLICKS AND A PREVIEW BETWEEN THEM.** The mockup's own
   * `{ u, li, idx, hover, mode, hours }`: the first click on a cell's copy or bin arms it, moving
   * the pointer along the row shows what WOULD happen — the value ghosted in, or a struck-through
   * zero — and the second click commits. Nothing is written until the second click, which is what
   * makes an armed range safe to abandon.
   */
  /**
   * ┌─────────────────────────────────────────────────────────────────┐
   * │ ⚠️⚠️ A DRAFT EXISTS BECAUSE AN EMPTY ALLOCATION CANNOT.                          │
   * └─────────────────────────────────────────────────────────────────┘
   *
   * The server refuses to make one with no hours — *“nothing to clear and nothing to add is not an
   * error”* — and could not show it if it did: `from_date` is derived from the days, and the read
   * skips a row that has none. So a booking with nowhere to type its first hour has to live on the
   * client until it has one.
   *
   * ⚠️⚠️ **AND THAT IS WHY *ÚJ ALLOKÁCIÓ HOZZÁADÁSA* DID NOTHING.** It posted zero hours with
   * `allocationId: null`, which is the request that means *make a new one* — and hit exactly the
   * two rules above. Reported as a dead button, and it was: the write was refused server-side and
   * the refusal is correct.
   *
   * ⚠️ **A draft retires when the pair GAINS a booking**, which is one rule for both ways of
   * making one. The picker's draft is created against a pair with none and goes when the first
   * arrives; the pop-over's is created against a pair with one and goes when the second does.
   * Counting beats matching here because a draft has no id to match ON — that is what makes it a
   * draft.
   */
  /** A booking that does not exist yet, shaped like one — the note above says why. */
  const draftOf = (lineItemId: string, real: readonly ResourceItem[]): ResourceItem => {
    /* ⚠️ The name comes from a sibling booking when there is one — *add another* knows the
         deliverable because it was opened on it — and from the picker's list otherwise. */
    const sibling = real.find((one) => one.lineItemId === lineItemId);
    const known = props.lineItems.find((one) => one.id === lineItemId);
    /* U-15 · the project's own list knows the address and the owner even with no booking. */
    const listed = props.view.projectLineItems?.find((one) => one.lineItemId === lineItemId);
    return {
      allocationId: '',
      lineItemId: lineItemId,
      lineItemName: sibling?.lineItemName ?? listed?.lineItemName ?? known?.name ?? '',
      /* ⚠️ A sibling booking on the same deliverable knows its number; the picker's row
           does not, so a first booking has no address to open until it is saved. */
      lineItemNumber: sibling?.lineItemNumber ?? listed?.lineItemNumber ?? null,
      lineItemIdentifier: sibling?.lineItemIdentifier ?? listed?.lineItemIdentifier ?? null,
      projectId: sibling?.projectId ?? listed?.projectId ?? null,
      projectName: sibling?.projectName ?? listed?.projectName ?? known?.projectName ?? null,
      projectColor: sibling?.projectColor ?? listed?.projectColor ?? null,
      contractCode: sibling?.contractCode ?? listed?.contractCode ?? null,
      /* The picker's label IS the contract's identifier — `lineItemContractLabel`. */
      contractIdentifier:
        sibling?.contractIdentifier ?? listed?.contractIdentifier ?? known?.contractLabel ?? null,
      /* ⚠️ A sibling booking on the same line item knows the owner; the picker's own row
           does not carry one, so a first booking shows the project alone until it is saved. */
      contractOwnerName: sibling?.contractOwnerName ?? listed?.contractOwnerName ?? null,
      /* ⚠️⚠️ A DRAFT HAS NO HISTORY, and this is the honest shape of that: nothing has
           been logged against a booking that does not exist, nothing is planned on it yet, and
           the dates are the ones the picker is about to set. The line item's own estimate is
           known, so the remainder is the whole of it. */
      contractType: sibling?.contractType ?? listed?.contractType ?? null,
      fromDate: null,
      toDate: null,
      trackedHours: '0.00',
      overtimeHours: '0.00',
      remainingHours:
        sibling?.estimateHours ?? listed?.estimateHours ?? known?.estimateHours ?? '0.00',
      overtimeTypeName: null,
      overtimeTypeId: null,
      /* ⚠️ A draft is what a brand-new booking WILL be: even, hard, no overtime — the same
           defaults `0129` and `0143` give the columns. */
      distribution: 'even' as const,
      booking: 'hard' as const,
      multiplier: '1.00',
      estimateHours: '0.00',
      plannedHours: '0.00',
      cells: props.view.columns.map(() => '0.00'),
    };
  };

  const itemsFor = (memberId: string): ResourceItem[] => {
    const row = props.view.rows.find((one) => one.memberId === memberId);
    const real = row?.items ?? [];
    return [
      ...real,
      ...drafts
        .filter((draft) => draft.memberId === memberId)
        .filter(
          (draft) =>
            real.filter((one) => one.lineItemId === draft.lineItemId).length <= draft.baseline,
        )
        .map((draft) => draftOf(draft.lineItemId, real)),
    ];
  };

  /**
   * ┌─────────────────────────────────────────────────────────────────┐
   * │ ⚠️⚠️ ONE ROW PER DELIVERABLE. THE CELL SUMS ITS BOOKINGS.                        │
   * └─────────────────────────────────────────────────────────────────┘
   *
   * The server sends one item per ALLOCATION, and this drew one row per item — so a second booking
   * on the same deliverable appeared as a second row saying the same deliverable's name twice. The
   * mockup does not: `mine = allocsOf(uid, li)` and the cell is
   * `mine.reduce((s, a) => s + allocGross(a, from, to), 0)`. One row, the sum, and its hover title
   * reads *“N allokáció — kattints a listához”*. The pop-over IS the list.
   *
   * ⚠️ **Which is also why the range tools stopped naming an allocation.** Copy and clear act on
   * the pair: each booking copies ITS OWN anchor-column hours, or is cleared — `resColsSet(u, li,
   * targets, a0 => allocGross(a0, anchor))`. A gesture on a summed cell that silently picked one of
   * the bookings would be the *“which one did that land in”* problem `0129` warned about, arriving
   * through the grid instead of through the write.
   */
  const groupsFor = (memberId: string): Group[] => {
    const out: Group[] = [];
    for (const item of itemsFor(memberId)) {
      const group = out.find((one) => one.lineItemId === item.lineItemId);
      if (group === undefined) {
        out.push({ ...item, bookings: [item] });
        continue;
      }
      group.bookings.push(item);
      group.cells = group.cells.map((value, index) =>
        (Number(value) + Number(item.cells[index] ?? '0')).toFixed(2),
      );
      group.plannedHours = (Number(group.plannedHours) + Number(item.plannedHours)).toFixed(2);
      /* ⚠️ The badge is *any* of them — a row with one booking at a premium is a row to look at. */
      if (group.overtimeTypeName === null && item.overtimeTypeName !== null) {
        group.overtimeTypeName = item.overtimeTypeName;
        group.multiplier = item.multiplier;
      }
    }
    return out;
  };

  /** How many bookings a pair holds right now — a draft's baseline. */
  const countFor = (memberId: string, lineItemId: string): number =>
    (props.view.rows.find((one) => one.memberId === memberId)?.items ?? []).filter(
      (one) => one.lineItemId === lineItemId,
    ).length;

  /** The row whose deliverable picker is open, and the button it hangs from. */
  const [picking, setPicking] = useState<string | null>(null);
  const pickAnchor = useRef<HTMLElement | null>(null);
  /* ⚠️ Round 5 item 29 — the overview table's own open state. */
  const [overview, setOverview] = useState(false);
  /* 17 Oct 2026 · on a phone the six figures fold away until asked for — they take half the
     screen there. The switch sits in the toolbar's ⋯, under the view toggle. */
  const mobile = useIsMobile();
  const [statsShown, setStatsShown] = useState(false);
  const [range, setRange] = useState<{
    memberId: string;
    lineItemId: string;
    anchor: number;
    hover: number;
    mode: 'copy' | 'clear';
    hours: number;
  } | null>(null);

  /* ⚠️ Escape abandons it, like every other armed mode in the product. */
  useEffect(() => {
    if (range === null) return;
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') setRange(null);
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
    };
  }, [range]);

  /**
   * ⚠️⚠️ **THE ANCHOR KEEPS ITS VALUE AND THE REST OF THE RANGE TAKES IT.** The mockup draws
   * the anchor outlined and unchanged, and ghosts only the cells after it — *copy this to there*
   * rather than *including here*, which is what the gesture reads as.
   *
   * ⚠️ **A column the person cannot work is skipped**, which is the *“obviously non working days
   * should be skipped”* half. Capacity is the only honest test the grid has: a weekend column, a
   * holiday, or somebody who has left all report zero, and all three are days a plan must not land
   * on. It matters for COPY only — clearing a day nobody works clears nothing and costs nothing.
   */
  const commitRange = (): void => {
    if (range === null) return;
    const row = props.view.rows.find((one) => one.memberId === range.memberId);
    const low = Math.min(range.anchor, range.hover);
    const high = Math.max(range.anchor, range.hover);

    /* ⚠️ Every booking on the pair, each carrying its OWN anchor value — the mockup's
       `a0 => allocGross(a0, anchor)`. A draft has nothing to copy and nothing to clear. */
    const bookings = itemsFor(range.memberId).filter(
      (one) => one.lineItemId === range.lineItemId && one.allocationId !== '',
    );

    const writes = [];
    for (let index = low; index <= high; index += 1) {
      /* ⚠️ 27 Oct 2026 · the anchor is skipped when COPYING — it is the value being copied — and
         cleared with the rest when CLEARING, so clicking the same cell twice clears that one. */
      if (index === range.anchor && range.mode === 'copy') continue;
      const column = props.view.columns[index];
      if (column === undefined) continue;
      if (range.mode === 'copy' && Number(row?.capacity[index] ?? '0') <= 0) continue;
      for (const booking of bookings) {
        const hours = range.mode === 'copy' ? Number(booking.cells[range.anchor] ?? '0') : 0;
        writes.push({
          allocationId: booking.allocationId,
          memberId: range.memberId,
          lineItemId: range.lineItemId,
          fromDate: column.from,
          toDate: column.to,
          hours,
        });
      }
    }

    setRange(null);
    if (writes.length > 0) props.onWriteMany(writes);
  };

  /**
   * ┌─────────────────────────────────────────────────────────────────┐
   * │ ⚠️⚠️ ONE PAIR'S EDITABLE LINE, SHARED BY BOTH GROUPINGS — U-15.                  │
   * └─────────────────────────────────────────────────────────────────┘
   *
   * The people grid draws it under a person, 44px high beside the line item's name; the project
   * tab's line-item grid draws it under a line item as the middle of a person's three 32px lines.
   * It is the same cell either way — the same pop-over, the same range tools, the same draft — so
   * it is one function rather than two copies that would drift on the first fix.
   *
   * ⚠️ `tone` is the line-item grid's load tint: the person's TOTAL verdict, painted behind the
   * hours this one deliverable holds. The people grid passes none — its summary line says it.
   */
  const editableCells = (
    row: { readonly memberId: string; readonly memberName: string },
    item: Group,
    height: 'h-8' | 'h-11',
    tone?: (index: number) => string | undefined,
  ): ReactNode => (
    <div className="flex">
      {item.cells.map((value, index) => {
        const hours = Number(value);
        /* ⚠️ A cell reads SOFT only when every booking in it is one — the
           mockup's `every(a => a.soft)`. One committed hour in the column and
           the figure is a commitment, whatever is pencilled in beside it. */
        const soft =
          hours > 0 &&
          item.bookings
            .filter((one) => Number(one.cells[index] ?? '0') > 0)
            .every((one) => one.booking === 'soft');

        /* ⚠️ A range only ever runs along ONE booking's row — copying a
           deliverable's hours onto a different deliverable is a different
           gesture, and the mockup does not offer it either. */
        const armed =
          range !== null && range.memberId === row.memberId && range.lineItemId === item.lineItemId;
        /* ⚠️ `armed` narrows `range` for everything below it — TypeScript
           carries an aliased condition through, so a second `!== null` here
           is a check the compiler can prove pointless. */
        const inRange =
          armed &&
          index >= Math.min(range.anchor, range.hover) &&
          index <= Math.max(range.anchor, range.hover);
        const isAnchor = armed && range.anchor === index;
        const ghost =
          inRange && !isAnchor
            ? range.mode === 'copy'
              ? formatHours(range.hours)
              : formatHours(0)
            : null;
        /* ⚠️ Read here, where `range` is still narrowed — a ternary four
           levels into a class string is past where the alias reaches. */
        const ghostTone =
          !inRange || isAnchor
            ? ''
            : range.mode === 'clear'
              ? 'text-destructive cursor-pointer line-through'
              : 'text-primary cursor-pointer';

        return (
          <div
            key={props.view.columns[index]?.key ?? String(index)}
            className={`group relative flex ${height} w-[104px] shrink-0 items-center justify-end border-l ${
              isAnchor
                ? range.mode === 'clear'
                  ? 'ring-destructive z-10 ring-2 ring-inset'
                  : 'ring-primary z-10 ring-2 ring-inset'
                : ''
            } ${
              inRange && !isAnchor
                ? range.mode === 'clear'
                  ? 'bg-destructive/10'
                  : 'bg-primary/10'
                : ''
            }`}
            style={inRange ? undefined : { background: tone?.(index) }}
            onMouseEnter={() => {
              if (armed) setRange((one) => (one === null ? one : { ...one, hover: index }));
            }}
          >
            <button
              type="button"
              disabled={!props.canManage}
              className={`flex h-full w-full items-center justify-end px-2 text-[11.5px] tabular-nums ${
                hours === 0 && ghost === null ? 'text-muted-foreground/45' : ''
              } ${soft ? 'italic underline decoration-dotted underline-offset-4' : ''} ${
                ghost === null
                  ? props.canManage
                    ? 'hover:bg-accent/40 cursor-pointer'
                    : ''
                  : ghostTone
              }`}
              aria-label={
                armed
                  ? t('resource.range.apply', {
                      column: columnLabel(index),
                    })
                  : t('resource.cell.edit', {
                      /* Under a line item the cell is one PERSON's — the row already names the deliverable. */
                      name: byItems ? row.memberName : item.lineItemName,
                      column: columnLabel(index),
                    })
              }
              onClick={(event) => {
                /**
                 * ⚠️⚠️ **AN ARMED RANGE OWNS THE CLICK.** Opening the
                 * pop-over instead would abandon the gesture on the click
                 * meant to finish it, which is the one moment a reader is
                 * certain of what they are doing.
                 */
                if (armed) {
                  commitRange();
                  return;
                }
                cellAnchor.current = event.currentTarget;
                setCell({
                  memberId: row.memberId,
                  memberName: row.memberName,
                  lineItemId: item.lineItemId,
                  index,
                });
              }}
            >
              {ghost ?? formatHours(hours)}
              {/* ⚠️ The dashed corner the mockup draws on a pencilled-in cell. */}
              {soft && ghost === null ? (
                <span
                  aria-hidden
                  className="border-muted-foreground/60 pointer-events-none absolute top-1 right-1 size-1.5 rounded-[2px] border border-dashed"
                />
              ) : null}
            </button>

            {/**
             * ⚠️⚠️ **THE TWO TOOLS, ON HOVER, AND ONLY ON A CELL WITH HOURS
             * IN IT.** `showTools: g > 0 && !rs` — there is nothing to copy
             * from an empty cell and nothing to clear, and a grid that offers
             * both on every one of a thousand cells is a grid of buttons.
             */}
            {!props.canManage || hours <= 0 || range !== null ? null : (
              <div className="bg-background/95 absolute top-0 left-1 hidden h-full items-center gap-0.5 group-hover:flex">
                <RangeTool
                  name="copy"
                  label={t('resource.range.copy')}
                  onClick={() => {
                    setRange({
                      memberId: row.memberId,
                      lineItemId: item.lineItemId,
                      anchor: index,
                      hover: index,
                      mode: 'copy',
                      hours,
                    });
                  }}
                />
                <RangeTool
                  name="trash"
                  label={t('resource.range.clear')}
                  onClick={() => {
                    setRange({
                      memberId: row.memberId,
                      lineItemId: item.lineItemId,
                      anchor: index,
                      hover: index,
                      mode: 'clear',
                      hours,
                    });
                  }}
                />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );

  /** The sticky heading row — the same in both groupings but for what the first column names. */
  const header = (title: string): ReactNode => (
    <div className="bg-muted sticky top-0 z-20 flex border-b">
      <div className="bg-muted text-muted-foreground sticky left-0 z-30 flex w-[240px] shrink-0 items-center border-r px-3 py-1.5 text-[10.5px] font-medium tracking-wide uppercase">
        {title}
      </div>
      <div className="bg-muted sticky left-[240px] z-30 w-[104px] shrink-0 border-r" />
      {props.view.columns.map((column, index) => (
        <div
          key={column.key}
          className={`flex w-[104px] shrink-0 flex-col justify-center gap-0.5 border-l px-2 py-1.5 ${
            column.current ? 'bg-primary/10' : ''
          }`}
        >
          {column.week === null ? (
            <>
              <span className="text-[11px] font-medium tabular-nums">{columnLabel(index)}</span>
              <span className="text-muted-foreground text-[10.5px] font-medium tracking-wide uppercase">
                {new Intl.DateTimeFormat(locale, { weekday: 'short' }).format(
                  new Date(`${column.from}T00:00:00Z`),
                )}
              </span>
            </>
          ) : (
            /* ⚠️ A week reads as a span: its first day at the left edge, its last at the
               right, the week number centred under both. The single "okt. 12. – okt. 18."
               string wrapped inside 104px. */
            <>
              <span
                className="flex justify-between text-[11px] font-medium tabular-nums"
                aria-label={columnLabel(index)}
              >
                <span>{edge(column.from)}</span>
                <span>{edge(column.to)}</span>
              </span>
              <span className="text-muted-foreground text-center text-[10.5px] font-medium tracking-wide uppercase">
                {t('resource.week', { week: column.week })}
              </span>
            </>
          )}
        </div>
      ))}
    </div>
  );

  const avatars = useAvatars(props.view.rows.map((row) => row.memberAvatarUrl));

  const toggle = (memberId: string): void => {
    setOpen((current) =>
      current.includes(memberId)
        ? current.filter((one) => one !== memberId)
        : [...current, memberId],
    );
  };

  /** One column end, `okt. 12.` — the week header draws both ends apart. */
  const edge = (iso: string): string =>
    new Intl.DateTimeFormat(locale, { month: 'short', day: 'numeric' }).format(
      new Date(`${iso}T00:00:00Z`),
    );

  const columnLabel = (index: number): string => {
    const column = props.view.columns[index];
    if (column === undefined) return '';
    const day = new Intl.DateTimeFormat(locale, { month: 'short', day: 'numeric' });
    return props.view.mode === 'day'
      ? day.format(new Date(`${column.from}T00:00:00Z`))
      : `${day.format(new Date(`${column.from}T00:00:00Z`))} – ${day.format(new Date(`${column.to}T00:00:00Z`))}`;
  };

  /**
   * ┌─────────────────────────────────────────────────────────────────┐
   * │ ⚠️⚠️ U-15 · THE LINE-ITEM GROUPING — the project tab only.                         │
   * └─────────────────────────────────────────────────────────────────┘
   *
   * *"One row is one line item (only having one cell, Kiosztott, for it), and then if you open the
   * dropdown it shows the individual people assigned to that line item per row (showing munkaidő,
   * kiosztott, szabadság for each person)."*
   *
   * ⚠️⚠️ **THE ROWS ARE THE PROJECT'S LINE ITEMS, NOT THE GRID'S BOOKINGS.** `projectLineItems`
   * is every one, so a deliverable nobody is planned onto yet still has a row to add somebody
   * under — which is the whole point of grouping this way round. A CLOSED one is drawn only while
   * somebody has hours on it inside the window: closing blocks nothing (U-17), and hiding a plan
   * that exists would make the people grid and this one disagree.
   *
   * ⚠️ **The search narrows the line items here** — the tab stops sending it to the server in this
   * grouping, where narrowing PEOPLE would empty the sub-rows instead.
   */
  const byItems = props.onGrouping !== undefined && props.grouping === 'items';
  const needle = props.query.trim().toLowerCase();
  const plannedInWindow = (lineItemId: string): boolean =>
    props.view.rows.some((row) => row.items.some((item) => item.lineItemId === lineItemId));
  const shownLineItems = (props.view.projectLineItems ?? []).filter(
    (lineItem) =>
      (!lineItem.closed || plannedInWindow(lineItem.lineItemId)) &&
      (needle === '' ||
        [lineItem.lineItemName, lineItem.contractIdentifier, lineItem.contractOwnerName]
          .filter((part) => part !== null)
          .join(' ')
          .toLowerCase()
          .includes(needle)),
  );

  /** The line item under which *Személy hozzáadása* is open. */
  const [addingTo, setAddingTo] = useState<string | null>(null);

  /**
   * ⚠️ **Everybody with a row who is on this deliverable** — booked at any time (`memberIds`),
   * booked inside the window, or just added as a draft. Somebody whose bookings all lie outside
   * the window still gets a sub-row, of empty cells, because they ARE on it.
   */
  const peopleOn = (lineItemId: string, memberIds: readonly string[]) =>
    props.view.rows.filter(
      (row) =>
        memberIds.includes(row.memberId) ||
        row.items.some((item) => item.lineItemId === lineItemId) ||
        drafts.some((draft) => draft.memberId === row.memberId && draft.lineItemId === lineItemId),
    );

  /**
   * ⚠️⚠️ **P-1 · ONLY THE PROJECT'S MEMBERS ARE OFFERED.** The rows are the team OR anybody
   * already planned onto the project, so a row is not proof of membership — `projectMemberIds`
   * is, and the server refuses everybody else (`assignee_not_a_project_member`). Somebody already
   * on the line item is not offered again; a second booking is the cell pop-over's *add another*.
   */
  const addablePeople = (lineItemId: string) => {
    const lineItem = props.view.projectLineItems?.find((one) => one.lineItemId === lineItemId);
    const already = peopleOn(lineItemId, lineItem?.memberIds ?? []).map((row) => row.memberId);
    const members = props.view.projectMemberIds ?? [];
    return props.view.rows
      .filter((row) => members.includes(row.memberId) && !already.includes(row.memberId))
      .map((row) => ({ id: row.memberId, name: row.memberName, roleName: row.roleName }));
  };

  /**
   * ⚠️ **The pair's bookings, or a draft standing in for them.** In the people grid a pair is only
   * drawn when it has a booking or a draft; in the line-item grid a person whose bookings all lie
   * outside the window is drawn too, and the pop-over needs something to type the first hour into.
   */
  const bookingsFor = (memberId: string, lineItemId: string): ResourceItem[] => {
    const found = itemsFor(memberId).filter((item) => item.lineItemId === lineItemId);
    return found.length > 0 ? found : [draftOf(lineItemId, [])];
  };

  /**
   * ⚠️ **The line item's own figures, in the shape the row's info panel reads** — the whole
   * deliverable rather than one person's booking, which is what a line-item row is about.
   */
  const summaryOf = (lineItem: ResourceLineItem): ResourceItem => ({
    ...draftOf(lineItem.lineItemId, []),
    fromDate: lineItem.fromDate,
    toDate: lineItem.toDate,
    estimateHours: lineItem.estimateHours,
    plannedHours: lineItem.plannedHours,
    overtimeHours: lineItem.overtimeHours,
    trackedHours: lineItem.trackedHours,
    remainingHours: lineItem.remainingHours,
  });

  /** A person's face — the picture when there is one, the generated one otherwise. */
  const face = (row: ResourceRow): ReactNode => {
    const avatar = avatars.get(row.memberAvatarUrl ?? '');
    return avatar === undefined ? (
      <span
        aria-hidden
        className="text-on-id grid size-6 shrink-0 place-items-center rounded-full text-[9.5px] font-semibold"
        style={{ background: identityColor(row.memberId) }}
      >
        <AvatarFace
          seed={row.memberAvatarSeed ?? undefined}
          fallback={initialsOf(row.memberName)}
          size={24}
        />
      </span>
    ) : (
      <img src={avatar} alt="" className="size-6 shrink-0 rounded-full object-cover" />
    );
  };

  /**
   * The marks beside a name — 30 Oct 2026, the product owner: an amber alert for OVER-planning,
   * a red warning triangle for under-planning.
   *
   * ⚠️⚠️ **THE TOOLTIP IS NOT AN ACCESSIBLE NAME.** It appears on hover and on focus, and the
   * icon inside it is `aria-hidden` like every other decorative glyph — so a screen reader met a
   * person's row and heard nothing at all about the one thing the triangle is there to say. Caught
   * by its own test, which could not find the label.
   */
  const overPlannedMark = (row: ResourceRow): ReactNode => (
    <>
      {!row.overPlanned ? null : (
        <Tooltip content={t('resource.row.overPlanned')}>
          <span role="img" aria-label={t('resource.row.overPlanned')} className="flex items-center">
            <Icon name="circle-alert" size={13} className="text-status-warn" />
          </span>
        </Tooltip>
      )}
      {/* 28 Oct 2026 · under-planning — the server counts only weeks still to come
          (`resource.service`). */}
      {!row.underPlanned ? null : (
        <Tooltip content={t('resource.row.underPlanned')}>
          <span
            role="img"
            aria-label={t('resource.row.underPlanned')}
            className="flex items-center"
          >
            <Icon name="triangle-alert" size={13} className="text-status-bad-text" />
          </span>
        </Tooltip>
      )}
    </>
  );

  /**
   * ┌─────────────────────────────────────────────────────────────────┐
   * │ ⚠️⚠️ ONE PERSON UNDER A LINE ITEM: THREE LINES, THE PEOPLE GRID'S OWN.             │
   * └─────────────────────────────────────────────────────────────────┘
   *
   * *Munkaidő · Kiosztott · Szabadság*, 32px each, for the reason the contract gives: capacity
   * and leave are different facts with different answers, and one combined line would not fit two
   * figures into a 104px column legibly anyway.
   *
   * ⚠️ **Kiosztott is the hours on THIS line item**, and editable through the same cell. ⚠️⚠️
   * **Its tint is the person's TOTAL load** — the people grid's colour for that column — because
   * the question a planner has before typing is *does this person have room*, which no single
   * deliverable's share can answer. The cell's title says both figures; the triangle beside the
   * name says the same as it does in the people grid.
   */
  const personUnder = (lineItemId: string, row: ResourceRow): ReactNode => {
    const group: Group = groupsFor(row.memberId).find((one) => one.lineItemId === lineItemId) ?? {
      ...draftOf(lineItemId, []),
      bookings: [],
    };
    return (
      <div key={row.memberId} className="bg-muted/25 flex border-t">
        <div className="bg-[color-mix(in_oklch,var(--muted)_25%,var(--background))] sticky left-0 z-10 flex w-[240px] shrink-0 items-start gap-2 border-r py-2 pr-2 pl-3">
          <button
            type="button"
            className="flex min-w-0 flex-1 items-center gap-2 text-left"
            onClick={() => {
              props.onOpenPerson(row.memberId);
            }}
          >
            {face(row)}
            <span className="min-w-0 flex-1">
              <span className="flex items-center gap-1">
                <span className="truncate text-[12.5px] font-medium">{row.memberName}</span>
                {overPlannedMark(row)}
              </span>
              {row.roleName === null ? null : (
                <span className="text-muted-foreground block truncate text-[11px]">
                  {row.roleName}
                </span>
              )}
            </span>
          </button>
        </div>
        <div className="bg-[color-mix(in_oklch,var(--muted)_25%,var(--background))] sticky left-[240px] z-10 w-[104px] shrink-0 border-r">
          {(['capacity', 'allocated', 'leave'] as const).map((key) => (
            <div
              key={key}
              className={`flex h-8 items-center justify-end px-2 text-[11.5px] font-medium ${
                key === 'allocated' ? '' : 'text-muted-foreground'
              }`}
            >
              {t(`resource.line.${key}`)}
            </div>
          ))}
        </div>
        <div>
          <div className="flex">
            {row.capacity.map((value, index) => (
              <div
                key={props.view.columns[index]?.key ?? String(index)}
                className={`flex h-8 w-[104px] shrink-0 items-center justify-end border-l px-2 text-[12px] tabular-nums ${
                  Number(value) === 0 ? 'text-muted-foreground/45' : 'text-muted-foreground'
                }`}
              >
                {formatHours(Number(value))}
              </div>
            ))}
          </div>
          {editableCells(row, group, 'h-8', (index) =>
            tint(
              LOAD_COLOR[
                loadOf(Number(row.allocated[index] ?? '0'), Number(row.capacity[index] ?? '0'))
              ],
            ),
          )}
          <div className="flex">
            {row.leave.map((value, index) => {
              const hours = Number(value);
              return (
                <div
                  key={props.view.columns[index]?.key ?? String(index)}
                  className="flex h-8 w-[104px] shrink-0 items-center justify-end border-l px-2 text-[12px] tabular-nums"
                  style={
                    hours > 0
                      ? { background: tint('var(--leave)'), color: 'var(--leave-ink)' }
                      : undefined
                  }
                >
                  {/* ⚠️ Nothing at zero — the people grid's rule. */}
                  {hours === 0 ? '' : formatHours(hours)}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  const lineItemRow = (lineItem: ResourceLineItem): ReactNode => {
    const key = `li:${lineItem.lineItemId}`;
    const expanded = open.includes(key);
    const summary = summaryOf(lineItem);
    /* ⚠️ The one line: every booking on it summed per column — read-only, like the people grid's
       Kiosztott summary, because a total is not something to type into. */
    const totals = props.view.columns.map((_, index) =>
      props.view.rows.reduce(
        (sum, row) =>
          sum +
          row.items
            .filter((item) => item.lineItemId === lineItem.lineItemId)
            .reduce((inner, item) => inner + Number(item.cells[index] ?? '0'), 0),
        0,
      ),
    );

    return (
      <div key={lineItem.lineItemId} className="hover:bg-accent/15 border-b transition-colors">
        <div className="flex">
          <div className="bg-background sticky left-0 z-10 flex h-11 w-[240px] shrink-0 items-center gap-2 border-r px-2">
            <Button
              variant="ghost"
              size="icon-sm"
              aria-label={t('resource.row.people')}
              aria-expanded={expanded}
              onClick={() => {
                toggle(key);
              }}
            >
              <Icon name={expanded ? 'chevron-down' : 'chevron-right'} size={14} />
            </Button>
            <span
              aria-hidden
              className="size-2 shrink-0 rounded-[3px]"
              style={{ background: lineItem.projectColor ?? 'var(--muted-foreground)' }}
            />
            <span className="min-w-0 flex-1">
              <span className="flex items-center gap-1.5">
                <span className="truncate text-[13px] font-medium">{lineItem.lineItemName}</span>
                {!lineItem.closed ? null : (
                  <span className="text-muted-foreground bg-muted shrink-0 rounded px-1.5 py-px text-[10px] font-medium">
                    {t('lineItem.closed')}
                  </span>
                )}
              </span>
              <span className="text-muted-foreground block truncate text-[10.5px]">
                {[lineItem.contractIdentifier, lineItem.contractOwnerName]
                  .filter((one) => one !== null && one !== '')
                  .join(' · ')}
              </span>
            </span>
            <LineItemInfo
              item={summary}
              label={t('resource.tip.title', { name: lineItem.lineItemName })}
              {...openLineItem(go, summary)}
            />
          </div>
          <div className="bg-background sticky left-[240px] z-10 flex h-11 w-[104px] shrink-0 items-center justify-end border-r px-2 text-[11.5px] font-medium">
            {t('resource.line.allocated')}
          </div>
          <div className="flex">
            {totals.map((hours, index) => (
              <div
                key={props.view.columns[index]?.key ?? String(index)}
                className={`flex h-11 w-[104px] shrink-0 items-center justify-end border-l px-2 text-[12px] tabular-nums ${
                  hours === 0 ? 'text-muted-foreground/45' : 'font-medium'
                }`}
              >
                {formatHours(hours)}
              </div>
            ))}
          </div>
        </div>

        {!expanded ? null : (
          <>
            {peopleOn(lineItem.lineItemId, lineItem.memberIds).map((row) =>
              personUnder(lineItem.lineItemId, row),
            )}
            {/* ⚠️ Not under a closed one: closing blocks nothing, but planning NEW people onto a
                deliverable somebody closed is the thing the tab's line-item picker already
                declines to offer (C55). */}
            {!props.canManage || lineItem.closed ? null : (
              <div className="bg-muted/25 flex border-t">
                <div className="bg-[color-mix(in_oklch,var(--muted)_25%,var(--background))] sticky left-0 z-10 flex h-10 w-[240px] shrink-0 items-center border-r pr-2 pl-10">
                  <PickerTrigger
                    label={t('resource.person.add')}
                    onOpen={(element) => {
                      pickAnchor.current = element;
                      setAddingTo(lineItem.lineItemId);
                    }}
                  />
                </div>
                <div className="bg-[color-mix(in_oklch,var(--muted)_25%,var(--background))] sticky left-[240px] z-10 h-10 w-[104px] shrink-0 border-r" />
              </div>
            )}
          </>
        )}
      </div>
    );
  };

  /**
   * ⚠️⚠️ **A FILTER WITH ONE OPTION IS A CONTROL THAT CANNOT DO ANYTHING.** The project's tab
   * passes no managers, projects or contracts — it IS the project filter — and three dropdowns
   * reading *Mindenki / Minden projekt / Minden szerződés* with nothing under them offer a reader
   * work that leads nowhere. Load and overtime stay: both are questions about the rows on screen,
   * whichever screen it is.
   */
  const filters = [
    ...(props.managers.length === 0
      ? []
      : [
          {
            key: 'manager',
            label: t('dash.filter.manager'),
            value: props.managerId,
            onChange: props.onManager,
            options: [
              { value: '', label: t('dash.filter.everybody') },
              ...props.managers.map((one) => ({ value: one.id, label: one.name })),
            ],
          },
        ]),
    ...(props.onTeam === undefined
      ? []
      : teamFilter(t, props.teams ?? [], props.teamId ?? '', props.onTeam)),
    ...(props.projects.length === 0
      ? []
      : [
          {
            key: 'project',
            label: t('dash.filter.project'),
            value: props.projectId,
            onChange: props.onProject,
            options: [
              { value: '', label: t('dash.filter.allProjects') },
              ...props.projects.map((one) => ({ value: one.id, label: one.name })),
            ],
          },
        ]),
    ...(props.contracts.length === 0
      ? []
      : [
          {
            key: 'contract',
            label: t('resource.filter.contract'),
            value: props.contractId,
            onChange: props.onContract,
            options: [
              { value: '', label: t('resource.filter.allContracts') },
              ...props.contracts.map((one) => ({ value: one.id, label: one.label })),
            ],
          },
        ]),
    {
      key: 'load',
      label: t('resource.filter.load'),
      value: props.load,
      onChange: props.onLoad,
      options: [
        { value: '', label: t('resource.filter.anyLoad') },
        { value: 'under', label: t('resource.load.under') },
        { value: 'over', label: t('resource.load.over') },
      ],
    },
    {
      key: 'overtime',
      label: t('resource.filter.overtime'),
      value: props.overtimeOnly ? 'ot' : '',
      onChange: (value: string) => {
        props.onOvertimeOnly(value === 'ot');
      },
      options: [
        { value: '', label: t('resource.filter.anyLoad') },
        { value: 'ot', label: t('resource.filter.overtimeOnly') },
      ],
    },
    ...(props.onShowTentative === undefined
      ? []
      : [
          {
            key: 'tentative',
            label: t('resource.filter.tentative'),
            value: props.showTentative === true ? 'show' : '',
            onChange: (value: string) => {
              props.onShowTentative?.(value === 'show');
            },
            options: [
              { value: '', label: t('resource.filter.tentativeHidden') },
              { value: 'show', label: t('resource.filter.tentativeShown') },
            ],
          },
        ]),
  ];

  const stats = [
    { key: 'people', value: String(props.view.stats.people), dot: undefined },
    { key: 'under', value: String(props.view.stats.under), dot: LOAD_COLOR.under },
    { key: 'over', value: String(props.view.stats.over), dot: LOAD_COLOR.over },
    {
      key: 'load',
      value:
        props.view.stats.averageLoad === null ? '–' : `${String(props.view.stats.averageLoad)}%`,
      dot: undefined,
    },
    { key: 'planned', value: formatHours(Number(props.view.stats.plannedHours)), dot: undefined },
    {
      key: 'overtime',
      value:
        Number(props.view.stats.plannedHours) === 0
          ? '0%'
          : `${String(
              Math.round(
                (Number(props.view.stats.overtimeHours) / Number(props.view.stats.plannedHours)) *
                  100,
              ),
            )}%`,
      dot: 'var(--overtime)',
    },
  ] as const;

  return (
    /* 17 Oct 2026 · landscape only on a phone — the product owner. Both the dashboard and a
       project's tab draw this screen, so both are gated here. */
    <LandscapeOnly message={t('mobile.rotate')}>
      <div data-screen="dash-resource" className="flex min-h-0 flex-1 flex-col">
        <div className="flex items-center gap-2 border-b pr-3">
          <ListToolbar
            className="flex-1 border-b-0"
            labels={{
              search: byItems ? t('resource.pick.search') : t('resource.search'),
              filter: t('list.filter'),
              views: t('list.views'),
              columns: t('list.columns'),
              clearAll: t('list.clearAll'),
              searchOptions: t('list.searchOptions'),
              clearFilter: (label: string) => t('list.clearFilter', { label }),
              rangeFrom: t('list.rangeFrom'),
              rangeTo: t('list.rangeTo'),
              switchView: t('list.switchView'),
              more: t('list.more'),
              back: t('list.back'),
              close: t('common.close'),
            }}
            search={props.query}
            onSearchChange={props.onQuery}
            views={[
              { value: 'week', label: t('dash.view.week') },
              { value: 'day', label: t('dash.view.day') },
            ]}
            view={props.view.mode}
            onViewChange={(value) => {
              props.onMode(value === 'day' ? 'day' : 'week');
            }}
            filters={filters}
            /* 17 Oct 2026 · the period rides the toolbar, so a phone places it beside the search. */
            trailing={
              <RangeControl
                /* ⚠️ The period ASKED for, so the control names it before the answer lands. */
                value={props.range ?? { from: props.view.from, to: props.view.to }}
                defaultRange={props.defaultRange}
                /* ⚠️⚠️ **ONE COLUMN PER PRESS — round 5 item 6.** A week in the weekly view and a
             day in the daily one, rather than the whole selected window: with sixteen weeks on
             screen, a full-window step replaces every column at once and leaves nothing in
             common between the two views. `range-control.tsx` argues it at the prop. */
                stepDays={props.view.mode === 'day' ? 1 : 7}
                onChange={(range) => {
                  props.onRange(range.from, range.to);
                }}
              />
            }
            {...(mobile && props.hideStats !== true
              ? {
                  actions: [
                    {
                      label: t(statsShown ? 'resource.stats.hide' : 'resource.stats.show'),
                      icon: 'chart-column' as const,
                      onSelect: () => {
                        setStatsShown((shown) => !shown);
                      },
                    },
                  ],
                }
              : {})}
            /**
             * ⚠️⚠️ **U-15 · THE SECOND DIMENSION, IN THE SAME MENU AS THE FIRST** (14 Oct 2026).
             * *Heti / Napi* says how wide a column is and *Sortételek / Emberek* says what a row is;
             * both sit in one menu, divided, and the button names both — *Heti nézet · Sortételek*.
             * Only where `onGrouping` is given, which is the project tab.
             */
            {...(props.onGrouping === undefined
              ? {}
              : {
                  secondaryViews: {
                    value: byItems ? 'items' : 'people',
                    items: [
                      { value: 'items', label: t('resource.group.items') },
                      { value: 'people', label: t('resource.group.people') },
                    ],
                    onChange: (value: string) => {
                      props.onGrouping?.(value === 'items' ? 'items' : 'people');
                    },
                  },
                })}
          />

          {/**
           * ⚠️⚠️ **THE GLOBAL INFO BUTTON, BESIDE THE FILTER — round 5 item 29.** The row's own
           * pop-over answers *what is this deliverable*; this answers it for all of them at once,
           * which is the question somebody has before they know which row to hover.
           *
           * ⚠️ **A button rather than a `Tooltip`**, unlike the row's: a table of every line item
           * is something to read and scroll, and a panel that vanishes when the pointer leaves it
           * cannot be read.
           */}
          {/* ⚠️⚠️ **THE PROJECT'S PAGE ONLY — reported 20 Sep.** *"I see the global info
            button in the global erőforrás dashboard, when I clearly asked that should only work in
            the project scoped version."* On the workspace grid the table would list every
            deliverable of every project, which is a different question from the one item 29 asks. */}
          {!props.overview ? null : (
            <Button
              variant="ghost"
              size="icon-sm"
              aria-label={t('resource.overview.open')}
              title={t('resource.overview.open')}
              onClick={() => {
                setOverview(true);
              }}
            >
              <Icon name="circle-help" size={15} />
            </Button>
          )}
        </div>

        {/* ── the six figures ─────────────────────────────────────────────────────────────── */}
        {props.hideStats === true || (mobile && !statsShown) ? null : (
          <div className="grid grid-cols-2 border-b md:grid-cols-3 xl:grid-cols-6">
            {stats.map((stat) => (
              <div
                key={stat.key}
                className="border-r border-b px-4 py-2.5 last:border-r-0 xl:border-b-0"
              >
                <div className="text-muted-foreground flex items-center gap-1.5 text-[11.5px]">
                  <span className="truncate">
                    {t(`resource.stat.${stat.key}` as 'resource.stat.people')}
                  </span>
                  {stat.dot === undefined ? null : (
                    <span
                      aria-hidden
                      className="size-1.5 shrink-0 rounded-full"
                      style={{ background: stat.dot }}
                    />
                  )}
                </div>
                {props.loading === true ? (
                  <div className="mt-0.5 flex h-[28.5px] items-center">
                    <Skeleton width="48px" className="h-4" />
                  </div>
                ) : (
                  <div className="mt-0.5 text-[19px] font-medium tabular-nums">{stat.value}</div>
                )}
              </div>
            ))}
          </div>
        )}

        {props.serverError === null ? null : (
          <p className="text-destructive border-b px-4 py-2 text-[12px]">{props.serverError}</p>
        )}

        {props.loading === true ? (
          <PeopleGridSkeleton />
        ) : byItems ? (
          shownLineItems.length === 0 ? (
            <div className="text-muted-foreground flex flex-1 flex-col items-center justify-center gap-2 p-8 text-center">
              <Icon name="list" size={22} className="opacity-60" />
              <span className="text-[13px]">{t('resource.emptyItems')}</span>
            </div>
          ) : (
            <div className="min-h-0 flex-1 overflow-auto">
              <div className="min-w-max">
                {header(t('effort.col.lineItem'))}
                {shownLineItems.map((lineItem) => lineItemRow(lineItem))}
              </div>
            </div>
          )
        ) : props.view.rows.length === 0 ? (
          <div className="text-muted-foreground flex flex-1 flex-col items-center justify-center gap-2 p-8 text-center">
            <Icon name="users" size={22} className="opacity-60" />
            <span className="text-[13px]">{t('resource.empty')}</span>
          </div>
        ) : (
          <div className="min-h-0 flex-1 overflow-auto">
            <div className="min-w-max">
              {header(t('dash.column.person'))}

              {props.view.rows.map((row) => {
                const expanded = open.includes(row.memberId);
                const items = groupsFor(row.memberId);

                const lines = [
                  { key: 'capacity', label: t('resource.line.capacity'), cells: row.capacity },
                  { key: 'allocated', label: t('resource.line.allocated'), cells: row.allocated },
                  { key: 'leave', label: t('resource.line.leave'), cells: row.leave },
                ] as const;

                return (
                  <div key={row.memberId} className="hover:bg-accent/15 border-b transition-colors">
                    <div className="flex">
                      <div className="bg-background sticky left-0 z-10 flex w-[240px] shrink-0 items-start gap-2 border-r px-2 py-2">
                        <Button
                          variant="ghost"
                          size="icon-sm"
                          aria-label={t('resource.row.breakdown')}
                          onClick={() => {
                            toggle(row.memberId);
                          }}
                        >
                          <Icon name={expanded ? 'chevron-down' : 'chevron-right'} size={14} />
                        </Button>
                        <button
                          type="button"
                          className="flex min-w-0 flex-1 items-center gap-2 text-left"
                          onClick={() => {
                            props.onOpenPerson(row.memberId);
                          }}
                        >
                          {face(row)}
                          <span className="min-w-0 flex-1">
                            <span className="flex items-center gap-1">
                              <span className="truncate text-[12.5px] font-medium">
                                {row.memberName}
                              </span>
                              {/* ⚠️ The triangle is OVER-planning only. Under-planning is a column of
                              spare capacity — worth a colour, not a warning. */}
                              {overPlannedMark(row)}
                            </span>
                            {row.roleName === null ? null : (
                              <span className="text-muted-foreground block truncate text-[11px]">
                                {row.roleName}
                              </span>
                            )}
                          </span>
                        </button>
                      </div>

                      <div className="bg-background sticky left-[240px] z-10 w-[104px] shrink-0 border-r">
                        {lines.map((line) => (
                          <div
                            key={line.key}
                            className={`flex h-8 items-center justify-end px-2 text-[11.5px] font-medium ${
                              line.key === 'allocated' ? '' : 'text-muted-foreground'
                            }`}
                          >
                            {line.label}
                          </div>
                        ))}
                      </div>

                      <div>
                        {lines.map((line) => (
                          <div key={line.key} className="flex">
                            {line.cells.map((value, index) => {
                              const hours = Number(value);
                              const verdict =
                                line.key === 'allocated'
                                  ? loadOf(hours, Number(row.capacity[index] ?? '0'))
                                  : null;
                              const color = verdict === null ? undefined : LOAD_COLOR[verdict];
                              const hasOvertime = Number(row.overtime[index] ?? '0') > 0;
                              /* ⚠️ Leave draws nothing at zero: every ordinary week would otherwise
                               carry a column of `0:00` saying nobody was away. */
                              const blank = line.key === 'leave' && hours === 0;

                              return (
                                <Tooltip
                                  key={props.view.columns[index]?.key ?? String(index)}
                                  content={t('resource.cell.summary', {
                                    capacity: formatHours(Number(row.capacity[index] ?? '0')),
                                    allocated: formatHours(Number(row.allocated[index] ?? '0')),
                                  })}
                                  asChild
                                >
                                  <div
                                    className={`relative flex h-8 w-[104px] shrink-0 items-center justify-end border-l px-2 text-[12px] tabular-nums ${
                                      hours === 0 && !blank ? 'text-muted-foreground/45' : ''
                                    } ${line.key === 'allocated' && hours > 0 ? 'font-medium' : ''}`}
                                    style={
                                      line.key === 'leave' && hours > 0
                                        ? {
                                            background: tint('var(--leave)'),
                                            color: 'var(--leave-ink)',
                                          }
                                        : { background: tint(color), color }
                                    }
                                  >
                                    {blank ? '' : formatHours(hours)}
                                    {line.key === 'allocated' && hasOvertime ? (
                                      <span
                                        aria-hidden
                                        className="absolute top-1 right-1 size-1.5 rounded-full"
                                        style={{ background: 'var(--overtime)' }}
                                      />
                                    ) : null}
                                  </div>
                                </Tooltip>
                              );
                            })}
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* ── Phase 6 · DM-5 · tentative deal bookings, dashed, never in the load ── */}
                    {props.showTentative !== true
                      ? null
                      : (row.tentative ?? []).map((deal) => (
                          <div key={deal.dealId} className="flex border-t border-dashed">
                            <div className="bg-background sticky left-0 z-10 flex h-9 w-[240px] shrink-0 items-center gap-2 border-r pr-2 pl-3">
                              <Icon
                                name="square-dashed"
                                size={12}
                                className="text-muted-foreground shrink-0"
                              />
                              <Tooltip content={t('resource.tentative.hint')} asChild>
                                <button
                                  type="button"
                                  className="min-w-0 flex-1 truncate text-left text-[12.5px] hover:underline"
                                  onClick={() => {
                                    go(`/deals/${deal.dealIdentifier}`);
                                  }}
                                >
                                  {deal.dealName}
                                </button>
                              </Tooltip>
                            </div>
                            <div className="bg-background text-muted-foreground sticky left-[240px] z-10 flex h-9 w-[104px] shrink-0 items-center justify-end border-r px-2 text-[11px]">
                              {t('resource.tentative.label')}
                            </div>
                            {deal.cells.map((value, index) => (
                              <div
                                key={props.view.columns[index]?.key ?? String(index)}
                                className={`text-muted-foreground flex h-9 w-[104px] shrink-0 items-center justify-end border-l border-dashed px-2 text-[12px] italic tabular-nums ${
                                  Number(value) === 0 ? 'opacity-40' : ''
                                }`}
                              >
                                {Number(value) === 0 ? '' : formatHours(Number(value))}
                              </div>
                            ))}
                          </div>
                        ))}

                    {/* ── one line per line item ──────────────────────────────────────────── */}
                    {!expanded ? null : (
                      <>
                        {items.map((item) => (
                          <div key={item.lineItemId} className="bg-muted/25 flex border-t">
                            <div
                              /* ⚠️⚠️ **`pl-3`, NOT `pl-10` — reported 20 Sep.** The 40px indent said
                               *this is a sub-row* and cost a quarter of a 240px column, so the
                               contract owner's name — the thing item 5 put there — was truncated
                               away on almost every row. The tint already says it is a sub-row. */
                              className="bg-[color-mix(in_oklch,var(--muted)_25%,var(--background))] sticky left-0 z-10 flex h-11 w-[240px] shrink-0 items-center gap-2 border-r pr-2 pl-3"
                            >
                              <span
                                aria-hidden
                                className="size-2 shrink-0 rounded-[3px]"
                                style={{
                                  background: item.projectColor ?? 'var(--muted-foreground)',
                                }}
                              />
                              <span className="min-w-0 flex-1">
                                {/* ⚠️ `markup.html` 3220 — `text-[13px] font-medium`. The deliverable
                                  is what the sub-row is ABOUT; drawn at the weight of the project
                                  caption under it the eye has nothing to land on. */}
                                <span className="block truncate text-[13px] font-medium">
                                  {item.lineItemName}
                                </span>
                                <span className="text-muted-foreground block truncate text-[10.5px]">
                                  {/* ⚠️⚠️ **THE PERSON, NOT THE CODE — round 5 item 5.** It read
                                    *Haystack · HSO-C01*; a contract code is a machine identifier,
                                    and what somebody scanning a week wants beside the project is
                                    who owns the work. */}
                                  {[item.projectName, item.contractOwnerName]
                                    .filter((one) => one !== null && one !== '')
                                    .join(' · ')}
                                </span>
                              </span>
                              {/**
                               * ┌────────────────────────────────────────────────────────────────┐
                               * │ ⚠️⚠️ THE ROW'S INFO BUTTON — round 5 item 28.                 │
                               * └────────────────────────────────────────────────────────────────┘
                               *
                               * *"A soról hiányzik az info gomb ami quick summary az adott sorhoz.
                               * Ez a mockupon helyesen szerepel."* `line-item-summary.tsx` is the
                               * panel, shared with item 29's global table.
                               *
                               * ⚠️⚠️ **HOVER IS A TOOLTIP AND A CLICK IS THE PANEL — second pass.**
                               * The first put the whole 260px panel inside a `Tooltip`, which flipped
                               * above the row, was clipped by the grid's overflow, and could not hold
                               * the *Sortétel megnyitása* link at all. `line-item-summary.tsx` argues
                               * the split at `LineItemInfo`.
                               */}
                              <LineItemInfo
                                item={item}
                                label={t('resource.tip.title', { name: item.lineItemName })}
                                /* ⚠️ The address needs BOTH halves — a booking whose contract the
                                 reader cannot see has no link rather than a broken one. */
                                {...openLineItem(go, item)}
                              />
                              {item.overtimeTypeName === null ? null : (
                                <Tooltip
                                  content={t('resource.item.overtime', {
                                    name: item.overtimeTypeName,
                                    multiplier: item.multiplier,
                                  })}
                                >
                                  <span
                                    role="img"
                                    aria-label={t('resource.item.overtime', {
                                      name: item.overtimeTypeName,
                                      multiplier: item.multiplier,
                                    })}
                                    className="flex items-center"
                                  >
                                    <Icon
                                      name="timer"
                                      size={12}
                                      className="shrink-0 text-pink-600"
                                    />
                                  </span>
                                </Tooltip>
                              )}
                              {/**
                               * ⚠️⚠️ **THE ROW MENU IS GONE, AND WHAT IT HELD MOVED TO THE CELL.**
                               * It carried planned overtime, the daily spread and delete — and it was
                               * right that those belong to the ALLOCATION rather than to a column.
                               * What it could not survive is a person holding two bookings on one
                               * deliverable (`0143`): the row is then a group, and a menu on it
                               * cannot say which of them it means. `allocation-cell.tsx` asks the
                               * cell instead, and lists them.
                               *
                               * ⚠️ The `timer` badge above stays. It is a READING, not a control, and
                               * the one thing the menu did that the pop-over cannot: say at a glance
                               * which rows are planned at a premium without opening anything.
                               */}
                            </div>
                            <div className="bg-[color-mix(in_oklch,var(--muted)_25%,var(--background))] text-muted-foreground sticky left-[240px] z-10 flex h-11 w-[104px] shrink-0 items-center justify-end border-r px-2 text-[11px] tabular-nums">
                              {/**
                               * ┌────────────────────────────────────────────────────────────┐
                               * │ ⚠️⚠️ THE LABEL, NOT THE SUM — round 5 item 27.             │
                               * └────────────────────────────────────────────────────────────┘
                               *
                               * This column held the whole plan — *20:00*, *50:00*, *392:00* — in the
                               * same sticky slot where the PERSON rows underneath carry their
                               * *Munkaidő / Kiosztott / Szabadság* labels. A figure sitting in a column
                               * of labels reads as a fourth label that happens to be a number, and a
                               * number with no name on it is one nobody can check: it is neither the
                               * window's total nor any column on screen.
                               *
                               * ⚠️ **The hours are not lost.** Every cell to the right is this line's
                               * plan, day by day, and the info pop-over beside the name gives the
                               * totals with their names attached.
                               *
                               * ⚠️ Global and project both, because one component draws both.
                               */}
                              {t('resource.line.allocated')}
                            </div>
                            {editableCells(row, item, 'h-11')}
                          </div>
                        ))}

                        {!props.canManage ? null : (
                          <div className="bg-muted/25 flex border-t">
                            <div className="bg-[color-mix(in_oklch,var(--muted)_25%,var(--background))] sticky left-0 z-10 flex h-10 w-[240px] shrink-0 items-center border-r pr-2 pl-10">
                              <PickerTrigger
                                label={t('resource.item.add')}
                                onOpen={(element) => {
                                  pickAnchor.current = element;
                                  setPicking(row.memberId);
                                }}
                              />
                            </div>
                            <div className="bg-[color-mix(in_oklch,var(--muted)_25%,var(--background))] sticky left-[240px] z-10 h-10 w-[104px] shrink-0 border-r" />
                          </div>
                        )}
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* ⚠️ No figure while loading — `0 people` is a claim about the team. As `logging-screen.tsx`. */}
        <div className="text-muted-foreground flex min-h-[33px] items-center gap-3 border-t px-4 py-2 text-[11.5px]">
          {props.loading === true ? null : (
            <>
              <span>
                {byItems
                  ? t('resource.footer.lineItems', { count: shownLineItems.length })
                  : t('dash.footer.people', { count: props.view.rows.length })}
              </span>
              <span>{t('dash.footer.columns', { count: props.view.columns.length })}</span>
            </>
          )}
        </div>

        {picking === null ? null : (
          <LineItemPicker
            anchor={pickAnchor}
            /* ⚠️ A deliverable this person already holds is not offered again HERE — a second
             booking on the same one is the pop-over's *add another*, which is a different
             gesture with a different answer to *how many hours*. */
            items={props.lineItems.filter(
              (one) =>
                !(props.view.rows.find((row) => row.memberId === picking)?.items ?? []).some(
                  (item) => item.lineItemId === one.id,
                ),
            )}
            onPick={(item) => {
              setDrafts((current) => [
                ...current,
                { memberId: picking, lineItemId: item.id, baseline: countFor(picking, item.id) },
              ]);
              setOpen((current) => (current.includes(picking) ? current : [...current, picking]));
              setPicking(null);
            }}
            onClose={() => {
              setPicking(null);
            }}
          />
        )}

        {addingTo === null ? null : (
          <PersonPicker
            anchor={pickAnchor}
            people={addablePeople(addingTo)}
            onPick={(person) => {
              setDrafts((current) => [
                ...current,
                {
                  memberId: person.id,
                  lineItemId: addingTo,
                  baseline: countFor(person.id, addingTo),
                },
              ]);
              const key = `li:${addingTo}`;
              setOpen((current) => (current.includes(key) ? current : [...current, key]));
              setAddingTo(null);
            }}
            onClose={() => {
              setAddingTo(null);
            }}
          />
        )}

        {cell === null ? null : (
          <AllocationCell
            target={cell}
            anchor={cellAnchor}
            columnLabel={columnLabel(cell.index)}
            canManage={props.canManage}
            overtimeTypes={props.overtimeTypes}
            /* ⚠️ EVERY booking on the pair, which is the whole reason this is a pop-over and not a
             menu — the server already sends one item per allocation. */
            items={bookingsFor(cell.memberId, cell.lineItemId)}
            onHours={(item, hours) => {
              const column = props.view.columns[cell.index];
              if (column === undefined) return;
              props.onWrite({
                /* ⚠️ A draft somebody ASKED for is a new booking (`null`). The line-item grid's
                 implicit one — a person whose bookings all lie outside the window — names the
                 pair instead (absent), so the hours join the booking they already hold. */
                allocationId:
                  item.allocationId !== ''
                    ? item.allocationId
                    : drafts.some(
                          (draft) =>
                            draft.memberId === cell.memberId &&
                            draft.lineItemId === cell.lineItemId,
                        )
                      ? null
                      : undefined,
                memberId: cell.memberId,
                lineItemId: cell.lineItemId,
                fromDate: column.from,
                toDate: column.to,
                hours,
              });
            }}
            onPatch={(item, patch) => {
              /* ⚠️ 27 Oct 2026 · a spread change re-spreads THIS column, not the whole plan. */
              const column = props.view.columns[cell.index];
              props.onPatchItem(
                item,
                patch.distribution !== undefined && column !== undefined
                  ? { ...patch, fromDate: column.from, toDate: column.to }
                  : patch,
              );
            }}
            onClear={(item) => {
              /* ⚠️ Zero hours for this column only — the save's span is the column. */
              const column = props.view.columns[cell.index];
              if (column === undefined) return;
              props.onWrite({
                allocationId: item.allocationId,
                memberId: cell.memberId,
                lineItemId: cell.lineItemId,
                fromDate: column.from,
                toDate: column.to,
                hours: 0,
              });
            }}
            onDelete={(item) => {
              /* ⚠️ A draft has nothing to delete — it was never written. Dropping the last one for
               the pair is what *remove this row* means while it is still a draft. */
              if (item.allocationId === '') {
                setDrafts((current) => {
                  /* ⚠️ The LAST one, walked from the end — `findLastIndex` is newer than this
                   project's TypeScript target and the loop says the same thing. */
                  let last = -1;
                  for (let index = current.length - 1; index >= 0; index -= 1) {
                    const draft = current[index];
                    if (draft?.memberId === cell.memberId && draft.lineItemId === cell.lineItemId) {
                      last = index;
                      break;
                    }
                  }
                  return last < 0 ? current : current.filter((_, index) => index !== last);
                });
                return;
              }
              props.onDeleteItem(item);
              setCell(null);
            }}
            onAdd={() => {
              /**
               * ⚠️⚠️ **A SECOND BOOKING CANNOT EXIST UNTIL IT HAS AN HOUR IN IT**, so this adds a
               * DRAFT rather than posting one. The first version posted zero hours with
               * `allocationId: null` and nothing happened — the server refuses to create an empty
               * allocation and could not return one if it did, because a row with no days has no
               * span and the read skips it. Both rules are right; the button was asking for
               * something that cannot exist. See `itemsFor`.
               */
              setDrafts((current) => [
                ...current,
                {
                  memberId: cell.memberId,
                  lineItemId: cell.lineItemId,
                  baseline: countFor(cell.memberId, cell.lineItemId),
                },
              ]);
            }}
            onClose={() => {
              setCell(null);
            }}
          />
        )}

        {/* ⚠️ Round 5 item 29 — every line item's figures at once. `line-item-overview.tsx` says
          why its rows are per deliverable rather than per booking. */}
        <LineItemOverview
          items={props.view.projectLineItems ?? []}
          open={overview}
          onClose={() => {
            setOverview(false);
          }}
        />
      </div>
    </LandscapeOnly>
  );
}
